import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiDownload, FiCalendar, FiTrendingUp, FiDollarSign, FiShoppingBag, FiChevronDown, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import '../components/Button.css';
import './Verkoopdata.css';
import { ProductNaamMetKlasse } from '../components/ProductKlasseIcon';

const Verkoopdata = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [expandedDay, setExpandedDay] = useState(null);
  const [dayDetails, setDayDetails] = useState({});
  const [loadingDay, setLoadingDay] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      
      const response = await api.get(`/woocommerce/sales/stats?${params.toString()}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    // Vereis altijd een datum range
    if (!dateRange.start_date && !dateRange.end_date) {
      alert('Selecteer eerst een datumperiode (bijv. "2025 t/m heden") voordat je importeert.');
      return;
    }
    
    const dateInfo = ` voor de periode ${dateRange.start_date || 'begin'} tot ${dateRange.end_date || 'nu'}`;
    
    const confirmMessage = `Weet je zeker dat je verkoopdata wilt importeren uit WooCommerce?${dateInfo}\n\n` +
      'Dit kan even duren bij veel orders (maximaal 5 minuten).\n' +
      'Orders met status completed, processing en on-hold worden geïmporteerd.\n\n' +
      'Tip: Nieuwe orders worden automatisch elke 4 uur gesynchroniseerd.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setImporting(true);
    setImportResults(null);
    try {
      const payload = {};
      if (dateRange.start_date) payload.after = dateRange.start_date + 'T00:00:00';
      if (dateRange.end_date) payload.before = dateRange.end_date + 'T23:59:59';

      console.log('[Verkoopdata] Start import met payload:', payload);
      // Langere timeout voor grote imports (5 minuten)
      const response = await api.post('/woocommerce/import/sales', payload, {
        timeout: 300000 // 5 minuten
      });
      
      console.log('[Verkoopdata] Import response:', response.data);
      setImportResults(response.data);
      
      if (response.data.message) {
        alert(response.data.message);
      } else {
        const ordersOk = response.data.totalOrdersProcessed ?? response.data.total ?? 0;
          const imported = response.data.totalItems || 0;
          const skipped = response.data.totalSkipped ?? 0;
          const fromApi = response.data.totalLineItemsFromApi ?? 0;
          let message = `Import voltooid!\n\n` +
            `Orders opgehaald: ${ordersOk}\n` +
            `Orderregels uit WooCommerce: ${fromApi}\n` +
            `Nieuw geïmporteerd: ${imported}\n` +
            `Overgeslagen (stonden al in de database): ${skipped}\n` +
            `Mislukt: ${response.data.failed || 0}`;
          if (ordersOk > 0 && fromApi <= 1 && imported + skipped <= 1) {
            message += '\n\nTip: WooCommerce stuurt bij dit endpoint mogelijk geen orderregels (line_items) mee. Controleer in de serverlogs of er "0 regel(s)" bij de orders staat.';
          } else if (skipped > 0 && imported === 0) {
            message += '\n\nDeze orders stonden al in de database. Voor een herimport: verkoopdata voor dit periode eerst wissen (bijv. via database).';
          }
          alert(message);
      }
      
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error importing:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert('Fout bij importeren:\n\n' + errorMessage + 
        '\n\nControleer:\n' +
        '- WooCommerce configuratie is correct\n' +
        '- API credentials hebben leesrechten\n' +
        '- Er zijn orders in WooCommerce met status completed/processing/on-hold');
    } finally {
      setImporting(false);
    }
  };

  const handleVerwijderVerkoopdata = async () => {
    const hasRange = dateRange.start_date && dateRange.end_date;
    const msg = hasRange
      ? `Verkoopdata verwijderen van ${dateRange.start_date} t/m ${dateRange.end_date}?\n\nDit kan niet ongedaan worden.`
      : 'ALLE verkoopdata verwijderen?\n\nDit wist de volledige verkoopdata in de database. Kan niet ongedaan worden.';
    if (!window.confirm(msg)) return;

    setDeleting(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      const response = await api.delete(`/woocommerce/sales/verkoopdata?${params.toString()}`);
      alert(`Verwijderd: ${response.data.deleted ?? 0} record(s).`);
      setDayDetails({});
      setExpandedDay(null);
      fetchStats();
    } catch (err) {
      console.error('Error deleting verkoopdata:', err);
      alert('Fout bij verwijderen: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getBronDisplay = (bron) => {
    const empty = bron == null || bron === '' || bron === '—' || bron === '-' || !String(bron).trim();
    if (empty) return 'geen bron';
    const s = String(bron).trim().toLowerCase();
    if (s === 'onbekend' || s === 'unknown') return 'bol.com';
    return bron;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('nl-NL');
  };

  const fetchDayDetails = useCallback(async (date) => {
    if (dayDetails[date]) {
      setExpandedDay(prev => prev === date ? null : date);
      return;
    }
    setLoadingDay(date);
    try {
      const response = await api.get(`/woocommerce/sales/day/${date}`);
      setDayDetails(prev => ({ ...prev, [date]: response.data.regels || [] }));
      setExpandedDay(date);
    } catch (err) {
      console.error('Error fetching day details:', err);
    } finally {
      setLoadingDay(null);
    }
  }, [dayDetails]);

  const toggleDay = (date) => {
    if (loadingDay) return;
    if (expandedDay === date) {
      setExpandedDay(null);
    } else {
      fetchDayDetails(date);
    }
  };

  if (loading && !stats) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="verkoopdata">
      <div className="page-header">
        <h1 className="page-title">Verkoopdata Analyse</h1>
        <button 
          className="btn btn-primary" 
          onClick={handleImport}
          disabled={importing}
        >
          <FiDownload className={importing ? 'spinning' : ''} />
          {importing ? 'Importeren...' : 'Importeer Verkoopdata'}
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={handleVerwijderVerkoopdata}
          disabled={deleting}
          title={dateRange.start_date && dateRange.end_date ? `Verwijder verkoopdata van ${dateRange.start_date} t/m ${dateRange.end_date}` : 'Verwijder alle verkoopdata'}
        >
          <FiTrash2 /> {deleting ? 'Verwijderen...' : 'Verkoopdata verwijderen'}
        </button>
      </div>

      {/* Info box over historische data */}
      <div className="info-box">
        <p>
          <strong>💡 Tip:</strong> Voor goede inkoopvoorstellen berekeningen heb je minstens 12 maanden historische verkoopdata nodig. 
          Hoe meer data, hoe beter! Importeer daarom eerst alle data van 2025 t/m heden voordat je inkoopvoorstellen genereert.
        </p>
        <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          <strong>🔄 Automatische sync:</strong> Nieuwe orders worden automatisch elke 4 uur gesynchroniseerd tot heden. 
          Je hoeft alleen handmatig te importeren voor historische data (bijv. bij eerste setup).
        </p>
      </div>

      {/* Date Filter */}
      <div className="date-filter">
        <div className="filter-group">
          <label>
            <FiCalendar /> Van datum:
          </label>
          <input
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>
            <FiCalendar /> Tot datum:
          </label>
          <input
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
          />
        </div>
        <div className="filter-presets">
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              const today = new Date();
              setDateRange({ 
                start_date: '2025-01-01', 
                end_date: today.toISOString().split('T')[0] 
              });
            }}
            title="Stel periode in op 2025 t/m vandaag (aanbevolen)"
          >
            2025 t/m heden
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              const now = new Date();
              const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), 1);
              setDateRange({ 
                start_date: oneYearAgo.toISOString().split('T')[0], 
                end_date: now.toISOString().split('T')[0] 
              });
            }}
            title="Laatste 12 maanden"
          >
            Laatste 12 maanden
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#3FDFCF' }}>
              <FiShoppingBag />
            </div>
            <div className="stat-content">
              <h3>Totaal Verkocht</h3>
              <p className="stat-value">{stats.totaal_verkocht || 0}</p>
              <p className="stat-label">items</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#28a745' }}>
              <FiDollarSign />
            </div>
            <div className="stat-content">
              <h3>Totaal Omzet</h3>
              <p className="stat-value">{formatCurrency(stats.totaal_omzet)}</p>
              <p className="stat-label">totaal</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f093fb' }}>
              <FiTrendingUp />
            </div>
            <div className="stat-content">
              <h3>Totaal Orders</h3>
              <p className="stat-value">{stats.totaal_orders || 0}</p>
              <p className="stat-label">orders</p>
            </div>
          </div>
        </div>
      )}

      {/* Meest Verkochte Producten */}
      {stats && stats.meest_verkocht && stats.meest_verkocht.length > 0 && (
        <div className="section">
          <h2>Meest Verkochte Producten</h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Aantal Verkocht</th>
                  <th>Omzet</th>
                </tr>
              </thead>
              <tbody>
                {stats.meest_verkocht.map((product, index) => (
                  <tr key={index}>
                    <td><ProductNaamMetKlasse productId={product.product_id} naam={product.product_naam} strong /></td>
                    <td>{product.product_sku || '-'}</td>
                    <td>{product.totaal_aantal}</td>
                    <td>{formatCurrency(product.totaal_omzet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Verkoop per Dag (openklapbaar per dag met orders + bron) */}
      {stats && stats.verkoop_per_dag && stats.verkoop_per_dag.length > 0 && (
        <div className="section">
          <h2>Verkoop per Dag</h2>
          <p className="section-hint">Klik op een rij om alle orders van die dag te zien, inclusief bron (google, direct, meta, pinterest, etc.). Staat er «geen bron»? Dan ontbreekt herkomst in WooCommerce of de order is geïmporteerd vóór de bron-import. Bij herkomst «onbekend» tonen we bol.com. Voor volledige bron: verkoopdata voor die periode verwijderen en opnieuw importeren.</p>
          <div className="table-container">
            <table className="data-table verkoop-per-dag-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Datum</th>
                  <th>Aantal Items</th>
                  <th>Omzet</th>
                  <th>Aantal Orders</th>
                </tr>
              </thead>
              <tbody>
                {stats.verkoop_per_dag.map((day, index) => {
                  const isExpanded = expandedDay === day.verkoopdatum;
                  const regels = dayDetails[day.verkoopdatum];
                  const isLoading = loadingDay === day.verkoopdatum;
                  return (
                    <React.Fragment key={index}>
                      <tr
                        className={`day-row ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleDay(day.verkoopdatum)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="expand-cell">
                          {isLoading ? (
                            <span className="spinning">⟳</span>
                          ) : isExpanded ? (
                            <FiChevronDown aria-hidden="true" />
                          ) : (
                            <FiChevronRight aria-hidden="true" />
                          )}
                        </td>
                        <td><strong>{formatDate(day.verkoopdatum)}</strong></td>
                        <td>{day.totaal_aantal}</td>
                        <td>{formatCurrency(day.totaal_omzet)}</td>
                        <td>{day.aantal_orders}</td>
                      </tr>
                      {isExpanded && Array.isArray(regels) && (
                        <tr className="day-detail-row">
                          <td colSpan={5} className="day-detail-cell">
                            <div className="day-detail-table-wrap">
                              <table className="data-table day-orders-table">
                                <thead>
                                  <tr>
                                    <th>Order ID</th>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Aantal</th>
                                    <th>Eenheidsprijs</th>
                                    <th>Totaal</th>
                                    <th>Bron</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {regels.map((regel) => (
                                    <tr key={regel.id}>
                                      <td>{regel.woocommerce_order_id}</td>
                                      <td><ProductNaamMetKlasse productId={regel.product_id} naam={regel.product_naam} /></td>
                                      <td>{regel.product_sku || '—'}</td>
                                      <td>{regel.aantal}</td>
                                      <td>{formatCurrency(regel.eenheidsprijs)}</td>
                                      <td>{formatCurrency(regel.totaal_prijs)}</td>
                                      <td><span className="bron-badge" title={`Herkomst: ${getBronDisplay(regel.bron)}`}>{getBronDisplay(regel.bron)}</span></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats && (!stats.meest_verkocht || stats.meest_verkocht.length === 0) && (
        <div className="empty-state">
          <p>Geen verkoopdata gevonden. Importeer eerst verkoopdata uit WooCommerce.</p>
        </div>
      )}

      {importResults && (
        <div className="import-results">
          <h3>Import Resultaten</h3>
          <p>Succesvol: {importResults.successful} | Mislukt: {importResults.failed} | Totaal items: {importResults.totalItems}</p>
        </div>
      )}
    </div>
  );
};

export default Verkoopdata;
